import type { IRatingMatrixFrameworkConsideration } from '__generated__';
import { baseApi, considerationListTag } from 'store/api';
import {
  API,
  APIRead,
  asPatchBody,
  getAllDynamic,
  normalizeError,
  type PatchBody,
} from 'utils/api-client';
import {
  buildConsiderationTemplateFilter,
  getSortOrderPatches,
  type SortOrderPatch,
} from './helpers';

export const ratingMatrixFrameworkConsiderationApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getConsiderations: builder.query<
      IRatingMatrixFrameworkConsideration[],
      { frameworkTemplateID: number }
    >({
      async queryFn({ frameworkTemplateID }) {
        try {
          const { value } = await getAllDynamic<IRatingMatrixFrameworkConsideration>(
            (body) => APIRead.api.ratingMatrixFrameworkConsiderationGetAllDynamicCreate(body),
            {
              filter: buildConsiderationTemplateFilter(frameworkTemplateID),
              orderBy: 'sortOrder asc',
            },
          );

          return { data: value };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      providesTags: (_result, _error, arg) => [considerationListTag(arg.frameworkTemplateID)],
    }),

    createConsideration: builder.mutation<
      IRatingMatrixFrameworkConsideration,
      Partial<IRatingMatrixFrameworkConsideration> & { frameworkTemplateID: number }
    >({
      async queryFn(body) {
        try {
          const { data } = await API.api.ratingMatrixFrameworkConsiderationCreateCreate(body);
          return { data };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      invalidatesTags: (_result, _error, arg) => [considerationListTag(arg.frameworkTemplateID)],
    }),

    patchConsideration: builder.mutation<
      string,
      {
        id: number;
        frameworkTemplateID: number;
        patch: PatchBody<IRatingMatrixFrameworkConsideration>;
      }
    >({
      async queryFn({ id, patch }) {
        try {
          const { data } = await API.api.ratingMatrixFrameworkConsiderationPatchPartialUpdate(
            { id },
            asPatchBody(patch),
          );
          return { data };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      invalidatesTags: (_result, _error, arg) => [considerationListTag(arg.frameworkTemplateID)],
    }),

    deleteConsideration: builder.mutation<
      IRatingMatrixFrameworkConsideration,
      { id: number; frameworkTemplateID: number }
    >({
      async queryFn({ id }) {
        try {
          const { data } = await API.api.ratingMatrixFrameworkConsiderationDeleteDelete({ id });
          return { data };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      invalidatesTags: (_result, _error, arg) => [considerationListTag(arg.frameworkTemplateID)],
    }),

    reorderConsiderations: builder.mutation<
      void,
      {
        frameworkTemplateID: number;
        items: IRatingMatrixFrameworkConsideration[];
        startIndex: number;
        endIndex: number;
      }
    >({
      async queryFn({ items, startIndex, endIndex }) {
        try {
          const patches = getSortOrderPatches(items, startIndex, endIndex);

          await Promise.all(
            patches.map((patch: SortOrderPatch) =>
              API.api.ratingMatrixFrameworkConsiderationPatchPartialUpdate(
                { id: patch.id },
                asPatchBody({ sortOrder: patch.sortOrder }),
              ),
            ),
          );

          return { data: undefined };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      invalidatesTags: (_result, _error, arg) => [considerationListTag(arg.frameworkTemplateID)],
    }),
  }),
});

export const {
  useGetConsiderationsQuery,
  useCreateConsiderationMutation,
  usePatchConsiderationMutation,
  useDeleteConsiderationMutation,
  useReorderConsiderationsMutation,
} = ratingMatrixFrameworkConsiderationApi;
